import type { DelegateTraceEntry } from '../lib/computer-use-trace.types';

/**
 * Trace entry kinds that represent the *completion* of a step rather than
 * active work. These are excluded from the live "current step" label while
 * the agent is still running — showing "Done" next to a spinning loader is
 * misleading.
 *
 * Error results (`isError: true`) are intentionally excluded from this set so
 * "Step failed" continues to appear on the live line (existing behaviour).
 */
export const TERMINAL_STEP_KINDS = ['tool_result', 'task_complete'] as const;

/**
 * Returns true when the entry marks the *successful completion* of a step or
 * turn rather than representing ongoing activity.
 *
 * Error results are NOT completion markers — "Step failed" stays visible on
 * the live line so the user sees mid-run failures.
 */
export const isLiveStepCompletionMarker = (entry: DelegateTraceEntry): boolean =>
  (TERMINAL_STEP_KINDS as ReadonlyArray<string>).includes(entry.kind) && !entry.isError;

/**
 * Returns the most recent trace entry that is not a successful completion
 * marker, or `undefined` when the trace is empty or every entry is a
 * completion marker.
 *
 * Use this to derive the "current activity" label for the live-step line.
 * Between-step gaps (where the latest entry is a "Done" marker) fall back to
 * the last real action rather than claiming the task is complete.
 */
export const selectLiveStepEntry = (
  trace: DelegateTraceEntry[]
): DelegateTraceEntry | undefined =>
  [...trace].reverse().find((entry) => !isLiveStepCompletionMarker(entry));

/**
 * Convenience wrapper over `selectLiveStepEntry` that returns just the label
 * string, or `undefined` when every entry is a completion marker (or the
 * trace is empty).
 */
export const selectLiveStepLabel = (trace: DelegateTraceEntry[]): string | undefined =>
  selectLiveStepEntry(trace)?.label;
